'use client';
import { createContext, ReactNode, Dispatch } from 'react';

import { BrokerAction } from '@/components/context/ContextBroker/ContextBroker';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { noop } from '@/util/misc';

import { Cart } from '@/types/Cart';
import { HTTP } from '@/const/http';
import { CACHE_TIME } from '@/const/cache';
import { datadogLogs } from '@datadog/browser-logs';

export type CartContextValue = Cart;

export type CartBrokerActions = {
  addToCart: (productId: string, clickId?: string) => Promise<void>;
  removeFromCart: (cartItemId: string) => Promise<void>;
  refreshCart: () => Promise<CartContextValue>;
};

export type CartBrokeredValue = {
  value: CartContextValue;
  actions: CartBrokerActions;
};

export const INITIAL_CART_VALUE: Cart = {
  id: null,
  name: null,
  description: null,
  customerId: null,
  items: []
};

export const CartContext = createContext<CartBrokeredValue>({
  value: { ...INITIAL_CART_VALUE },
  actions: {
    addToCart: noop,
    removeFromCart: noop,
    refreshCart: noop
  }
});

type CartProviderProps = {
  /** Put the component tree in here. Server components are supported */
  children: ReactNode;

  dispatch: Dispatch<BrokerAction>;
};

function CartProvider(props: CartProviderProps) {
  const { children, dispatch } = props;
  const brokerValue = useContextBroker();

  const decoratedValue: CartBrokeredValue = {
    value: brokerValue.cart,
    actions: {
      refreshCart: async (): Promise<CartContextValue> => {
        const cartResponse = await fetch('/api/cart', {
          method: 'GET',
          headers: {
            'content-type': 'application/json'
          },
          next: { revalidate: CACHE_TIME.NO_CACHE }
        });

        const json = (await cartResponse.json()) as {
          success: boolean;
          data: Cart;
        };

        dispatch({ type: 'CART_REFRESH', newContextValue: json.data });

        return json.data;
      },

      addToCart: async (
        productId: unknown,
        clickId?: string
      ): Promise<void> => {
        if (!brokerValue.user) {
          datadogLogs.logger.warn('add_to_cart_no_user', {
            category: 'add_to_cart_diagnostic',
            layer: 'CartContext',
            clickId: clickId ?? null,
            productId
          });
          throw Error('User is not logged in!');
        }

        datadogLogs.logger.info('cart_context_addToCart_entry', {
          category: 'add_to_cart_diagnostic',
          layer: 'CartContext',
          clickId: clickId ?? null,
          productId,
          userId: brokerValue.user.id
        });

        const addResponse = await fetch('/api/cart/items', {
          method: 'POST',
          body: JSON.stringify({
            productId
          }),
          headers: {
            'content-type': 'application/json',
            ...(clickId ? { 'x-click-id': clickId } : {})
          }
        });

        let responseBodyText: string | null = null;
        try {
          responseBodyText = await addResponse.clone().text();
        } catch {
          // ignore clone failure
        }

        if (addResponse.status !== HTTP.OK) {
          datadogLogs.logger.error('cart_context_addToCart_non_ok', {
            category: 'add_to_cart_diagnostic',
            layer: 'CartContext',
            clickId: clickId ?? null,
            productId,
            userId: brokerValue.user.id,
            status: addResponse.status,
            responseBody: responseBodyText
          });
          throw Error('Error adding to cart');
        }

        datadogLogs.logger.info('cart_context_addToCart_ok', {
          category: 'add_to_cart_diagnostic',
          layer: 'CartContext',
          clickId: clickId ?? null,
          productId,
          userId: brokerValue.user.id,
          status: addResponse.status
        });

        // If add was successful, refresh cart!
        await decoratedValue.actions.refreshCart();
      },

      removeFromCart: async (cartItemId: string): Promise<void> => {
        if (!brokerValue.user) {
          throw Error('User is not logged in!');
        }

        const removeResponse = await fetch('/api/cart/items', {
          method: 'DELETE',
          body: JSON.stringify({
            cartItemId
          }),
          headers: {
            'content-type': 'application/json'
          }
        });

        if (removeResponse.status !== HTTP.OK) {
          throw Error('Error removing from cart');
        }

        // If remove was successful, refresh cart!
        await decoratedValue.actions.refreshCart();
      }
    }
  };

  return (
    <CartContext.Provider value={decoratedValue}>
      {children}
    </CartContext.Provider>
  );
}

export default CartProvider;
